'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Volume2, VolumeX, Copy, Check, Download } from 'lucide-react';
import { downloadFromUrl } from '@/lib/downloadVideo';
import { BrandIcon } from '@/components/studio/BrandIcon';
import { useI18n } from '@/contexts/I18nContext';
import { useInViewport } from '@/hooks/useInViewport';
import { useWavespeedCatalog } from '@/hooks/useWavespeedCatalog';

interface Example {
  id: string;
  modelId: string;
  prompt: string | null;
  outputs: string[];
  thumbnailUrl?: string;
  createdAt: string;
}

interface ModelMeta {
  id: string;
  label: string;
  type: 'image' | 'video';
}

interface ExampleWithModel extends Example {
  modelLabel: string;
}

// Module-level memo so toggling between image / video doesn't refetch the
// same type we just loaded. Survives across remounts of the page.
const aggregateMemo: Partial<Record<'image' | 'video', { key: string; items: ExampleWithModel[] }>> = {};

export function ModelExamples({ type }: { type: 'image' | 'video' }) {
  const { t } = useI18n();
  const { models: catalog, updatedAt } = useWavespeedCatalog();
  const catalogKey = `${type}:${updatedAt ?? ''}`;
  const [items, setItems] = useState<ExampleWithModel[]>(() => aggregateMemo[type]?.items ?? []);
  const [loading, setLoading] = useState(() => !aggregateMemo[type]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cached = aggregateMemo[type];
    if (cached?.key === catalogKey) {
      setItems(cached.items);
      setLoading(false);
      return;
    }
    if (!catalog.length) return;
    // Clear stale items from the previous type — otherwise the grid would
    // briefly render the old type's URLs in the new type's element (e.g.
    // a video URL inside an <img>, producing broken-image icons).
    setItems([]);
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const models: ModelMeta[] = catalog.filter((m) => m.type === type && m.featured).slice(0, 12);
        // Streaming render: fire all requests in parallel but commit each
        // model's examples as soon as IT resolves — no waiting for the
        // slowest. The grid grows from first paint instead of staying as a
        // skeleton until every model returns.
        //
        // Interleaving still matters (top of the grid shows variety, not
        // all-of-model-A then all-of-model-B), so we keep a per-model
        // bucket map and re-interleave on every arrival. Cost is O(total
        // examples) per arrival, negligible at this scale.
        const buckets = new Map<string, { model: ModelMeta; data: Example[] }>();
        let resolvedCount = 0;

        const reinterleave = (): ExampleWithModel[] => {
          const merged: ExampleWithModel[] = [];
          // Stable order: keep the model order from the catalog so
          // interleaving doesn't shuffle on every arrival.
          const ordered = models
            .map((m) => buckets.get(m.id))
            .filter((b): b is { model: ModelMeta; data: Example[] } => !!b);
          const maxLen = Math.max(0, ...ordered.map((b) => b.data.length));
          for (let i = 0; i < maxLen; i++) {
            for (const b of ordered) {
              const ex = b.data[i];
              // The request already identifies the model, even when upstream
              // examples (or cached responses) omit their modelId.
              if (ex) merged.push({
                ...ex,
                modelId: typeof ex.modelId === 'string' && ex.modelId.trim() ? ex.modelId : b.model.id,
                modelLabel: b.model.label,
              });
            }
          }
          return merged;
        };

        await Promise.all(
          models.map((m) =>
            fetch(
              `/api/ai/wavespeed/examples?model=${encodeURIComponent(m.id)}&size=12`,
            )
              .then((r) => r.json())
              .then((j) => {
                if (cancelled) return;
                const data = (j?.data ?? []) as Example[];
                buckets.set(m.id, { model: m, data });
                resolvedCount += 1;
                const merged = reinterleave();
                setItems(merged);
                // Drop the skeleton as soon as we have ANY rendered tile —
                // user sees the first batch immediately, the rest fills in.
                if (merged.length > 0) setLoading(false);
                // Memoize when every model has reported (success or empty).
                if (resolvedCount === models.length) aggregateMemo[type] = { key: catalogKey, items: merged };
              })
              .catch(() => {
                if (cancelled) return;
                resolvedCount += 1;
                if (resolvedCount === models.length) {
                  aggregateMemo[type] = { key: catalogKey, items: reinterleave() };
                }
              }),
          ),
        );
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally only on `type` — refetching on `t` (i18n) would re-hit
    // the network on every parent re-render (model pick, prompt typing, …).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, catalog, catalogKey]);

  // One row of skeleton is enough — matches the responsive column count.
  const renderSkeletonGrid = () => (
    <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={`aspect-[213/300] animate-pulse rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)] ${
            i >= 2 ? 'hidden sm:block' : ''
          } ${i >= 3 ? 'sm:hidden md:block' : ''} ${
            i >= 4 ? 'md:hidden lg:block' : ''
          }`}
        />
      ))}
    </div>
  );

  let body: ReactNode;
  if (loading && items.length === 0) {
    body = renderSkeletonGrid();
  } else if (error) {
    body = (
      <div className="rounded-2xl border border-[#F0324B]/40 bg-[#F0324B]/10 py-12 text-center text-sm text-[#FFB4BE]">
        {error}
      </div>
    );
  } else if (items.length === 0) {
    body = (
      <div className="rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)] py-12 text-center text-sm text-[var(--st-muted)]">
        {t('studio.examples.none')}
      </div>
    );
  } else {
    // Cap to 4 rows × 5 columns = 20 tiles. The merge already interleaves
    // models, so the top 20 naturally cover the widest variety of models.
    body = (
      <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {items.slice(0, 20).map((item) => (
          <ExampleCard key={`${item.modelId}:${item.id}`} item={item} type={type} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3.5">
        <h2 className="text-[18px] font-bold text-white">{t('studio.examples.heading')}</h2>
        <p className="mt-1 text-[12.5px] font-medium text-[var(--st-muted)]">
          {t('studio.examples.hint')}
        </p>
      </div>
      {body}
    </div>
  );
}

function ExampleCard({
  item,
  type,
}: {
  item: ExampleWithModel;
  type: 'image' | 'video';
}) {
  const { t } = useI18n();
  const [muted, setMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Only fetch the example clip once the card nears the viewport — a wall of
  // examples would otherwise pull metadata for every video at once.
  const [cardRef, inView] = useInViewport<HTMLDivElement>();

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.prompt) return;
    navigator.clipboard?.writeText(item.prompt).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };
  const output = item.outputs[0];
  const isVideo = type === 'video';
  const hasThumb = Boolean(item.thumbnailUrl);

  const handleEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
  };

  const handleLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };

  return (
    <div
      ref={cardRef}
      onMouseEnter={isVideo ? handleEnter : undefined}
      onMouseLeave={isVideo ? handleLeave : undefined}
      className="group relative aspect-[213/300] overflow-hidden rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)] transition duration-200 ease-out hover:z-10 hover:scale-[1.03] hover:border-[var(--st-violet)] hover:shadow-[0_24px_50px_-14px_rgba(110,67,232,0.55)]"
    >
      {/* Light skeleton until the first frame / image / poster actually paints */}
      {!loaded && !hasThumb && (
        <div
          aria-hidden
          className="absolute inset-0 animate-pulse"
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(168,85,247,0.08) 50%, rgba(255,255,255,0.05) 100%)',
          }}
        />
      )}
      {isVideo ? (
        inView && (
          <video
            ref={videoRef}
            src={output}
            // Our cached poster lets us paint a still and skip pulling the clip
            // until hover (preload="none"); without one we fall back to metadata.
            poster={item.thumbnailUrl || undefined}
            className={`h-full w-full object-cover transition-opacity duration-300 ${
              loaded || hasThumb ? 'opacity-100' : 'opacity-0'
            }`}
            loop
            muted={muted}
            playsInline
            preload={hasThumb ? 'none' : 'metadata'}
            onLoadedData={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        )
      ) : (
        <img
          src={output}
          referrerPolicy="no-referrer"
          alt=""
          draggable={false}
          loading="lazy"
          className={`h-full w-full object-cover transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      )}

      {/* Top-right: which model produced this example */}
      <div
        title={item.modelLabel}
        className="pointer-events-none absolute left-2 top-2 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-black/55 ring-1 ring-white/15 backdrop-blur-md transition-all duration-200 group-hover:h-7 group-hover:w-auto group-hover:max-w-[90%] group-hover:gap-1.5 group-hover:px-1.5"
      >
        <BrandIcon modelId={item.modelId} size={14} rounded="md" />
        <span className="hidden truncate text-[10px] font-semibold text-white group-hover:inline group-hover:pr-1">
          {item.modelLabel}
        </span>
      </div>

      {/* Copy the example's prompt (bottom-left, on hover) */}
      {item.prompt && (
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? t('studio.examples.copied') : t('studio.examples.copyPrompt')}
          className="absolute bottom-2 left-2 flex h-7 items-center gap-1 rounded-full bg-black/45 px-2 text-[11px] font-semibold text-white opacity-0 ring-1 ring-white/20 backdrop-blur-md transition hover:bg-black/60 group-hover:opacity-100"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? t('studio.examples.copied') : t('studio.examples.copyPrompt')}
        </button>
      )}

      {isVideo && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (output) downloadFromUrl(output, `${item.modelId || 'example'}-${item.id}.mp4`);
            }}
            title={t('videos.download')}
            aria-label={t('videos.download')}
            className="absolute bottom-2 right-11 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white opacity-0 ring-1 ring-white/20 backdrop-blur-md transition hover:bg-black/55 group-hover:opacity-100"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setMuted((value) => !value)}
            title={muted ? t('landing.examples.sound.unmute') : t('landing.examples.sound.mute')}
            className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white opacity-0 ring-1 ring-white/20 backdrop-blur-md transition hover:bg-black/55 group-hover:opacity-100"
          >
            {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </button>
        </>
      )}
    </div>
  );
}
